import { formatDate } from "@/lib/utils";
import React, { useState, useMemo } from "react";
import {
  PlusCircle,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  FileText,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  ShoppingCart,
  Download,
  Printer,
  FileDown,
  Loader2,
  X,
} from "lucide-react";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import {
  FilterCard,
  FilterSearch,
  FilterActions,
} from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import { useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { all_routes } from "../../Router/all_routes";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { PageContainer } from "@/components/layout/PageContainer";

const statusVariants = {
  draft: "secondary",
  sent: "info",
  accepted: "success",
  rejected: "destructive",
  expired: "warning",
};

const QuotationsList = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [deleteConfirmationId, setDeleteConfirmationId] = useState(null);

  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [currentQuotationData, setCurrentQuotationData] = useState(null);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });


  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "";

  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (perm) => permissions.includes(perm);

  const { data: quotationsData, isLoading } = useQuery({
    queryKey: [
      "quotations",
      pagination.pageIndex + 1,
      pagination.pageSize,
      searchText,
      statusFilter,
    ],
    queryFn: async () => {
      const params = {
        page: pagination.pageIndex + 1,
        per_page: pagination.pageSize,
        with: ["customer", "store", "user"],
      };
      if (searchText) params.search = searchText;
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;

      const res = await apiClient.get(API_ENDPOINTS.QUOTATIONS.QUOTATIONS, {
        params,
      });
      return res.data;
    },
    refetchOnMount: "always",
    staleTime: 0,
  });

  const quotations = quotationsData?.data || [];
  const meta = quotationsData?.meta || {};
  const pageCount = meta.last_page || 1;

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await apiClient.delete(API_ENDPOINTS.QUOTATIONS.QUOTATION_DETAILS(id));
    },
    onSuccess: async () => {
      toast.success("Quotation deleted successfully");
      await queryClient.invalidateQueries({
        queryKey: ["quotations"],
        refetchType: "all",
      });
      setDeleteConfirmationId(null);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to delete quotation",
      );
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }) => {
      const res = await apiClient.patch(
        API_ENDPOINTS.QUOTATIONS.QUOTATION_STATUS(id),
        { status },
      );
      return res.data;
    },
    onSuccess: async () => {
      toast.success("Quotation status updated");
      await queryClient.invalidateQueries({
        queryKey: ["quotations"],
        refetchType: "all",
      });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to update quotation status",
      );
    },
  });

  const pdfMutation = useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.QUOTATION_PDF(id),
        {
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            Accept: "application/pdf, */*",
          },
          responseType: "blob",
          timeout: 30000,
          maxRedirects: 0,
          validateStatus: function (status) {
            return status >= 200 && status < 300;
          },
        },
      );

      if (response.status === 200) {
        const pdfBlob = new Blob([response.data], {
          type: "application/pdf",
        });

        if (pdfBlob.size === 0) {
          throw new Error("Received empty PDF file");
        }

        const blobUrl = URL.createObjectURL(pdfBlob);
        setPdfBlobUrl(blobUrl);
        return blobUrl;
      }
    },
    onError: (error) => {
      console.error("PDF Report error:", error);
      let errorMessage = "Failed to generate PDF report";

      if (error.code === "ECONNABORTED") {
        errorMessage = "Request timeout. Please try again.";
      } else if (error.response?.status === 204) {
        errorMessage = "No content available for PDF generation.";
      } else if (error.response?.status === 404) {
        errorMessage = "Quotation not found.";
      } else if (error.response?.status === 500) {
        errorMessage = "Server error. Please try again later.";
      }

      toast.error(errorMessage);
    },
  });

  const handleGeneratePDFReport = async (id) => {
    pdfMutation.mutate(id);
  };

  const downloadPDF = () => {
    if (pdfBlobUrl && currentQuotationData) {
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      link.download = `quotation-${currentQuotationData.quotation_number || currentQuotationData.id?.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Quotation downloaded successfully!");
    }
  };

  const printPDF = () => {
    if (pdfBlobUrl) {
      const printWindow = window.open(pdfBlobUrl, "_blank");
      if (!printWindow) {
        alert("Please allow popups to print the quotation");
        return;
      }
      printWindow.onload = function () {
        setTimeout(() => {
          printWindow.print();
        }, 500);
      };
    }
  };

  const handleClosePDFModal = () => {
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    pdfMutation.reset();
    setPdfModalOpen(false);
    setCurrentQuotationData(null);
  };

  const handlePDFAction = async (quotation) => {
    setCurrentQuotationData(quotation);
    setPdfModalOpen(true);
    handleGeneratePDFReport(quotation.id);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "quotation_number",
        header: "Quotation #",
        cell: ({ getValue, row }) => (
          <span className="font-medium">
            {getValue() || `QT-${row.original.id?.slice(0, 8)}`}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.customer?.name || row.customer?.full_name,
        header: "Customer",
        cell: ({ getValue }) =>
          getValue() || <span className="text-muted-foreground">-</span>,
      },
      {
        accessorFn: (row) => row.store?.name,
        header: "Store",
      },
      {
        accessorKey: "quotation_date",
        header: "Date",
        cell: ({ getValue }) =>
          getValue() ? formatDate(getValue()) : "-",
      },
      {
        accessorKey: "total",
        header: "Total",
        cell: ({ getValue }) =>
          getValue() != null
            ? `${currencySymbol}${parseFloat(getValue()).toFixed(2)}`
            : "-",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => {
          const status = getValue();
          return (
            <Badge
              variant={statusVariants[status] || "default"}
              className="capitalize"
            >
              {status?.replace(/_/g, " ") || "draft"}
            </Badge>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const quotation = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                {hasPermission("quotation read") && (
                  <DropdownMenuItem
                    onClick={() => {
                      setSelectedQuotation(quotation);
                      setShowDetailModal(true);
                    }}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    View Details
                  </DropdownMenuItem>
                )}
                {hasPermission("report quotation view") && (
                  <DropdownMenuItem onClick={() => handlePDFAction(quotation)}>
                    <FileDown className="mr-2 h-4 w-4" />
                    Download PDF
                  </DropdownMenuItem>
                )}
                {hasPermission("quotation update") && (
                  <DropdownMenuItem
                    onClick={() =>
                      navigate(
                        all_routes.editQuotation.replace(":id", quotation.id),
                      )
                    }
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                )}
                {hasPermission("quotation update_status") && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground font-normal py-1">
                      Change Status
                    </DropdownMenuLabel>
                    {quotation.status !== "draft" && (
                      <DropdownMenuItem
                        onClick={() =>
                          updateStatusMutation.mutate({
                            id: quotation.id,
                            status: "draft",
                          })
                        }
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        Set as Draft
                      </DropdownMenuItem>
                    )}
                    {quotation.status !== "sent" && (
                      <DropdownMenuItem
                        onClick={() =>
                          updateStatusMutation.mutate({
                            id: quotation.id,
                            status: "sent",
                          })
                        }
                      >
                        <Send className="mr-2 h-4 w-4" />
                        Set as Sent
                      </DropdownMenuItem>
                    )}
                    {quotation.status !== "accepted" && (
                      <DropdownMenuItem
                        onClick={() =>
                          updateStatusMutation.mutate({
                            id: quotation.id,
                            status: "accepted",
                          })
                        }
                      >
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Set as Accepted
                      </DropdownMenuItem>
                    )}
                    {quotation.status !== "rejected" && (
                      <DropdownMenuItem
                        onClick={() =>
                          updateStatusMutation.mutate({
                            id: quotation.id,
                            status: "rejected",
                          })
                        }
                      >
                        <XCircle className="mr-2 h-4 w-4" />
                        Set as Rejected
                      </DropdownMenuItem>
                    )}
                    {quotation.status !== "expired" && (
                      <DropdownMenuItem
                        onClick={() =>
                          updateStatusMutation.mutate({
                            id: quotation.id,
                            status: "expired",
                          })
                        }
                      >
                        <Clock className="mr-2 h-4 w-4" />
                        Set as Expired
                      </DropdownMenuItem>
                    )}
                  </>
                )}
                {hasPermission("quotation convert") && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() =>
                        navigate(
                          all_routes.convertToSale.replace(":id", quotation.id),
                        )
                      }
                    >
                      <ShoppingCart className="mr-2 h-4 w-4" />
                      Convert to Sale
                    </DropdownMenuItem>
                  </>
                )}
                {hasPermission("quotation delete") && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-red-600 focus:text-red-600"
                      onClick={() => setDeleteConfirmationId(quotation.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currencySymbol, navigate, updateStatusMutation, permissions],
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Quotations"
          subtitle="Manage and track customer quotations"
        />
        <PageHeaderActions>
          {hasPermission("quotation create") && (
            <Button onClick={() => navigate(all_routes.newQuotation)}>
              <PlusCircle className="mr-2 h-4 w-4" />
              New Quotation
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <FilterSearch
          placeholder="Search quotations..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <FilterActions>
          <Select
            value={statusFilter}
            onValueChange={(val) => {
              setStatusFilter(val);
              setPagination((p) => ({ ...p, pageIndex: 0 }));
            }}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </FilterActions>
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={quotations}
          enableRowSelection={true}
          pageCount={pageCount}
          pagination={pagination}
          onPaginationChange={setPagination}
          isLoading={isLoading}
          onRowClick={(row) => {
            setSelectedQuotation(row);
            setShowDetailModal(true);
          }}
          footerInfo={
            <div className="flex items-center gap-4">
              <span className="font-medium text-foreground">
                Total Quotations: {meta.total || 0}
              </span>
            </div>
          }
        />
      </TableCard>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto px-1">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Quotation Details -{" "}
              {selectedQuotation?.quotation_number || "Loading..."}
            </DialogTitle>
            <DialogDescription>
              Full details for this quotation including customer, items, and
              financial summary.
            </DialogDescription>
          </DialogHeader>

          {selectedQuotation && (
            <div className="space-y-6 px-5 pb-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      Quotation Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Quotation #:
                      </span>
                      <span className="font-medium">
                        {selectedQuotation.quotation_number ||
                          `QT-${selectedQuotation.id?.slice(0, 8)}`}
                      </span>
                    </div>
                    {selectedQuotation.reference_number && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Reference #:
                        </span>
                        <span className="font-medium">
                          {selectedQuotation.reference_number}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Status:</span>
                      <Badge
                        variant={
                          statusVariants[selectedQuotation.status] || "default"
                        }
                        className="capitalize"
                      >
                        {selectedQuotation.status?.replace(/_/g, " ") ||
                          "draft"}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Quotation Date:
                      </span>
                      <span className="font-medium">
                        {selectedQuotation.quotation_date
                          ? formatDate(selectedQuotation.quotation_date,)
                          : "-"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Valid Until:
                      </span>
                      <span className="font-medium">
                        {selectedQuotation.valid_until
                          ? formatDate(selectedQuotation.valid_until,)
                          : "-"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Store:</span>
                      <span className="font-medium">
                        {selectedQuotation.store?.name || "-"}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      Customer Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Name:</span>
                      <span className="font-medium">
                        {selectedQuotation.customer?.name ||
                          selectedQuotation.customer?.full_name ||
                          "Walk-in Customer"}
                      </span>
                    </div>
                    {selectedQuotation.customer && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Email:</span>
                          <span className="font-medium">
                            {selectedQuotation.customer.email || "-"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Phone:</span>
                          <span className="font-medium">
                            {selectedQuotation.customer.phone || "-"}
                          </span>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>

              {hasPermission("quotation item read") &&
                selectedQuotation.items?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">
                        Quotation Items
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="rounded-md border overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-12">#</TableHead>
                              <TableHead>Item</TableHead>
                              <TableHead className="text-right">Qty</TableHead>
                              <TableHead className="text-right">
                                Unit Price
                              </TableHead>
                              <TableHead className="text-right">
                                Total
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {selectedQuotation.items.map((item, idx) => (
                              <TableRow key={idx}>
                                <TableCell className="font-medium">
                                  {idx + 1}
                                </TableCell>
                                <TableCell>
                                  <div className="font-medium">
                                    {item.item_name ||
                                      item.product?.name ||
                                      "Unnamed item"}
                                  </div>
                                  {item.item_description && (
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                      {item.item_description}
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  {item.quantity}
                                </TableCell>
                                <TableCell className="text-right">
                                  {currencySymbol}
                                  {parseFloat(item.unit_price || 0).toFixed(2)}
                                </TableCell>
                                <TableCell className="text-right font-semibold">
                                  {currencySymbol}
                                  {parseFloat(item.line_total || 0).toFixed(2)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>
                )}

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Financial Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal:</span>
                    <span className="font-medium">
                      {currencySymbol}
                      {parseFloat(selectedQuotation.subtotal || 0).toFixed(2)}
                    </span>
                  </div>
                  {selectedQuotation.discount_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Discount:</span>
                      <span className="font-medium text-destructive">
                        -{currencySymbol}
                        {parseFloat(selectedQuotation.discount_amount).toFixed(
                          2,
                        )}
                      </span>
                    </div>
                  )}
                  {selectedQuotation.tax_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tax:</span>
                      <span className="font-medium">
                        {currencySymbol}
                        {parseFloat(selectedQuotation.tax_amount).toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t mt-2">
                    <span className="font-semibold">Total:</span>
                    <span className="font-bold text-lg text-green-600">
                      {currencySymbol}
                      {parseFloat(selectedQuotation.total || 0).toFixed(2)}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {selectedQuotation.notes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">{selectedQuotation.notes}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          {hasPermission("report quotation view") && (
            <DialogFooter className="px-5 pb-5">
              <Button
                variant="outline"
                onClick={() => handlePDFAction(selectedQuotation)}
                disabled={!selectedQuotation}
              >
                <FileDown className="mr-2 h-4 w-4" />
                Download PDF
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteConfirmationId}
        onOpenChange={() => setDeleteConfirmationId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              quotation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteMutation.mutate(deleteConfirmationId)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PDF Modal */}
      <Dialog open={pdfModalOpen} onOpenChange={handleClosePDFModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Quotation PDF
            </DialogTitle>
            <DialogDescription>
              Generate, download, or print the PDF document for this quotation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {currentQuotationData && (
              <div className="bg-muted p-4 rounded-lg">
                <h6 className="font-semibold mb-2">
                  Quotation #
                  {currentQuotationData.quotation_number ||
                    `QT-${currentQuotationData.id?.slice(0, 8)}`}
                </h6>
                <p className="text-muted-foreground text-sm">
                  <strong>Total:</strong> {currencySymbol}
                  {parseFloat(currentQuotationData.total || 0).toFixed(2)}
                </p>
              </div>
            )}

            {pdfMutation.isPending && (
              <div className="bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 p-4 rounded-lg flex items-center border border-blue-200 dark:border-blue-800">
                <Loader2 className="mr-3 h-4 w-4 animate-spin" />
                <span>Generating PDF quotation...</span>
              </div>
            )}

            {pdfMutation.isSuccess && !pdfMutation.isPending && (
              <div>
                <div className="bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 p-4 rounded-lg flex items-center border border-green-200 dark:border-green-800 mb-4">
                  <FileText className="mr-2 h-4 w-4" />
                  PDF quotation generated successfully!
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <Button className="w-full" onClick={downloadPDF}>
                    <FileDown className="mr-2 h-4 w-4" />
                    Download PDF
                  </Button>
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={printPDF}
                  >
                    <Printer className="mr-2 h-4 w-4" />
                    Print PDF
                  </Button>
                </div>
              </div>
            )}

            {pdfMutation.isError &&
              !pdfMutation.isPending &&
              !pdfMutation.isSuccess && (
                <div className="bg-destructive/10 text-destructive p-4 rounded-lg border border-destructive/20">
                  <div className="flex items-center">
                    <X className="mr-2 h-4 w-4" />
                    PDF generation failed. Please try again.
                  </div>
                  <div className="mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        pdfMutation.reset();
                        handleGeneratePDFReport(currentQuotationData.id);
                      }}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Retry
                    </Button>
                  </div>
                </div>
              )}
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default QuotationsList;
